import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Check, Sparkles, Zap, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";

export default function Upgrade() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/subscription/status").then(({ data }) => setPkg(data));
  }, []);

  async function upgrade() {
    setCreating(true);
    try {
      // Emergent-managed Sandbox Checkout (Flow A). Backend creates a
      // Stripe Checkout Session for the immersive_plus_monthly price and
      // returns the hosted URL. No card data touches our servers.
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "immersive_plus_monthly",
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      throw new Error("No checkout URL returned.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout.");
      setCreating(false);
    }
  }

  const isPlus = !!user?.is_plus;
  const isAdmin = !!user?.is_admin;

  return (
    <div className="mx-auto max-w-md px-6 pb-24 pt-8">
      <button data-testid="upgrade-back-btn" onClick={() => nav(-1)} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10">
        <ArrowLeft size={18} />
      </button>

      {/* Pre-launch banner — subscriptions aren't live yet. Adult-content
          payment processor is still being set up. Admins bypass this. */}
      {!isAdmin && (
        <div data-testid="upgrade-coming-soon-banner" className="mt-6 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-medium">Coming Soon</div>
          <div className="mt-1 text-sm text-amber-100 leading-relaxed">
            Immersive+ isn't live yet — we're still finalizing our payment provider.
            The perks below preview what launch will look like. Follow along on the app for updates!
          </div>
        </div>
      )}
      {isAdmin && (
        <div data-testid="upgrade-admin-banner" className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-medium">Admin — Test Mode</div>
          <div className="mt-1 text-sm text-emerald-100 leading-relaxed">
            Payments are locked for everyone else. You (admin) can still run checkout with test card <span className="font-mono tracking-wider">4242 4242 4242 4242</span>.
          </div>
        </div>
      )}

      <div className="mt-8 text-center">
        <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 border border-violet-400/30 px-4 py-1.5 text-xs uppercase tracking-widest text-violet-200">
          <Sparkles size={14} />
          Immersive+
        </div>
        <h1 className="mt-4 font-display text-5xl leading-tight text-white">
          Unlimited <br /><em className="not-italic bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">stories</em>.
        </h1>
        <p className="mt-3 text-neutral-400 leading-relaxed">
          One flat monthly price. Everything unlocked.
        </p>
      </div>

      <div className="mt-8 rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-6">
        <div className="text-center">
          <div className="font-display text-6xl text-white">
            $14.99
            <span className="font-body text-sm text-neutral-500 ml-1">/ month</span>
          </div>
          {isPlus && (
            <div data-testid="upgrade-active-badge" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 text-xs text-emerald-300">
              <Check size={12} /> Active
              {user?.plus_expires_at && (
                <span className="text-emerald-200/70 ml-1">
                  · renews {new Date(user.plus_expires_at).toLocaleDateString()}
                </span>
              )}
              {user?.preview_mode && <span className="text-emerald-200/70 ml-1">· preview</span>}
            </div>
          )}
        </div>

        <ul className="mt-6 space-y-3 text-sm">
          <Perk icon={<InfinityIcon size={14} />} text="Unlimited chatting (free: 30 messages / day)" />
          <Perk icon={<Sparkles size={14} />} text="Unlimited characters (free: 2)" />
          <Perk icon={<Sparkles size={14} />} text="Unlimited personas (free: 2)" />
          <Perk icon={<Zap size={14} />} text="Unlimited story recaps & regenerations" />
          <Perk icon={<Zap size={14} />} text="Priority AI response streaming" />
          <Perk icon={<Sparkles size={14} />} text="Unlimited NSFW roleplay depth" />
        </ul>

        {isPlus ? (
          <button data-testid="upgrade-manage-btn" disabled className="btn-pill mt-6 w-full py-3.5 bg-white/5 border border-white/10 text-white/70">
            You're on Immersive+
          </button>
        ) : isAdmin ? (
          <button
            data-testid="upgrade-checkout-btn"
            onClick={upgrade}
            disabled={creating}
            className="btn-pill btn-primary mt-6 w-full py-3.5 disabled:opacity-50"
          >
            {creating ? "Opening checkout…" : "Upgrade to Immersive+ (Admin test)"}
          </button>
        ) : (
          <button
            data-testid="upgrade-coming-soon-btn"
            disabled
            className="btn-pill mt-6 w-full py-3.5 bg-white/5 border border-white/10 text-white/60 cursor-not-allowed"
          >
            Coming soon
          </button>
        )}

        {user?.preview_mode && (
          <p className="mt-3 text-center text-[11px] text-emerald-300/80">
            Preview mode: all Plus features are already unlocked for testing.
          </p>
        )}
      </div>

      <p className="mt-5 text-center text-xs text-neutral-600">
        Cancel anytime. Payment renews monthly at $14.99 USD.
      </p>
      <div className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1 text-[11px] text-neutral-500">
        <Link to="/terms" className="hover:text-violet-300" data-testid="upgrade-terms-link">Terms</Link>
        <span className="text-neutral-700">·</span>
        <Link to="/privacy" className="hover:text-violet-300" data-testid="upgrade-privacy-link">Privacy</Link>
        <span className="text-neutral-700">·</span>
        <Link to="/refunds" className="hover:text-violet-300" data-testid="upgrade-refunds-link">Refund Policy</Link>
        <span className="text-neutral-700">·</span>
        <Link to="/contact" className="hover:text-violet-300" data-testid="upgrade-contact-link">Contact</Link>
      </div>
    </div>
  );
}

function Perk({ icon, text }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-violet-200">{icon}</span>
      <span className="text-neutral-200">{text}</span>
    </li>
  );
}
